// ─── Inline Figure Renderer ────────────────────────────────────────────────────
// Canonical figure token builder for all thesis figure types.
// Produces <div data-thesis-figure> tokens that ThesisFigureExtension renders.
//
// PIPELINE A (Ch1-3): buildConceptualFigureHTML → mermaid diagram token
// PIPELINE B (Ch4+):  buildResearchFigureHTML  → chart-svg token

import { injectFigureAfterParagraph } from './figurePipeline'

// ── Stable hash for anchor tracking ──────────────────────────────────────────

export function stableHash(str) {
  let h = 5381
  for (let i = 0; i < str.length; i++) h = ((h << 5) + h) ^ str.charCodeAt(i)
  return (h >>> 0).toString(36)
}

// ── Token builders ────────────────────────────────────────────────────────────

/**
 * Build a mermaid figure token for conceptual chapters (Ch1-3).
 */
export function buildConceptualFigureHTML({ figureId, mermaidCode, title, caption, chapterNumber, figureNumber }) {
  const label      = `Figure ${chapterNumber}.${figureNumber}: ${title}`
  const anchorHash = stableHash(`${figureId}-${chapterNumber}-${figureNumber}`)
  return [
    `<div`,
    ` data-thesis-figure="true"`,
    ` data-figure-id="${figureId}"`,
    ` data-figure-type="mermaid"`,
    ` data-figure-code="${encodeURIComponent(mermaidCode)}"`,
    ` data-figure-title="${encodeURIComponent(label)}"`,
    ` data-figure-caption="${encodeURIComponent(caption)}"`,
    ` data-anchor-hash="${anchorHash}"`,
    ` data-figure-number="${chapterNumber}.${figureNumber}"`,
    `></div>`,
  ].join('')
}

/**
 * Build a chart-svg figure token for research chapters (Ch4+).
 */
export function buildResearchFigureHTML({ figureId, svgCode, title, caption, chapterNumber, figureNumber }) {
  const label      = `Figure ${chapterNumber}.${figureNumber}: ${title}`
  const anchorHash = stableHash(`${figureId}-${chapterNumber}-${figureNumber}`)
  return [
    `<div`,
    ` data-thesis-figure="true"`,
    ` data-figure-id="${figureId}"`,
    ` data-figure-type="chart-svg"`,
    ` data-figure-code="${encodeURIComponent(svgCode)}"`,
    ` data-figure-title="${encodeURIComponent(label)}"`,
    ` data-figure-caption="${encodeURIComponent(caption)}"`,
    ` data-anchor-hash="${anchorHash}"`,
    ` data-figure-number="${chapterNumber}.${figureNumber}"`,
    `></div>`,
  ].join('')
}

/**
 * Wrap a figure token in a positioned container div for inline insertion.
 */
export function wrapFigureToken(tokenHTML) {
  return `\n<div class="thesis-figure-anchor" style="margin:2rem 0;clear:both">${tokenHTML}</div>\n`
}

/**
 * Inject a figure token into chapter HTML after the best-matching paragraph.
 * Re-exports injectFigureAfterParagraph for convenience.
 */
export { injectFigureAfterParagraph }
